import { makeSttClient } from "../api/sttClientFactory";
import type { StreamingSttClient } from "../api/streamingSttClient";
import { AppError } from "../errors/types";

// 氏名認識 (spec §4.4 Step 4): continuous 本編と同じ VAD + Scribe を「1 発話 = 1 WS session」
// で単発利用する軽量経路。keyterms は使わない (氏名は未知の固有名詞で効かない、spec D6)。
// Scribe の onclose は onError を呼ばないため timeout が無限待機の安全網 (必須)。

// **セグメントの受け手** (spec 2026-08-22 D3)。VAD の持ち主が「いま聴取している人」へ
// セグメントを配るための最小の面。
export type SegmentListener = {
  onSpeechStart?: () => void;
  onSpeechRealStart?: () => void;
  onSpeechEnd?: (audio: Float32Array) => void;
};

// **マイクのハンドル** (spec 2026-08-22 D1 / D2)。
//
// **この関数は VAD を作らない。** 作るのは持ち主 (usePreflightVoice) で、ここは
// 「起動を保証して、セグメントの受け手として並ぶ」だけを行う。
//
// **なぜ持ち主を外に出したのか**: 1 発話ごとに `MicVAD.new()` すると、プリロールの
// 環リングバッファ (最大 480ms) が**毎回空から始まる**。言い直しは案内が短く本人も
// 身構えているのですぐ答えるので、起動待ちがそのまま応答窓に落ちて**録音の頭が欠ける**
// (実測 11/46。言い直しでの発生率は 1 回目の 4.5 倍 = 2/24 対 9/22。spec 2026-08-22 §1)。
export type MicHandle = {
  /** VAD の起動を保証する。起動済みなら即 resolve、失敗したら reject する。 */
  ensure: () => Promise<void>;
  /** セグメントの受け手として並ぶ。返り値を呼ぶと外れる。 */
  attach: (listener: SegmentListener) => () => void;
};

export type SingleUtteranceOpts = {
  mic: MicHandle;
  onPartial?: (text: string) => void;
  // 発話検出の通知。**しきい値超えの最初の 1 フレーム (16 kHz / 512 samples = 32ms)**
  // で発火するので、咳・物音・スピーカーから回り込んだ AI 自身の声でも鳴る。
  // 表示の切り替えには十分だが、**読み上げを止める判断には使わないこと**
  // (2026-08-20 の実測で復唱確認の 26% がこれで切れていた)。認識自体の挙動は変えない。
  onSpeechStart?: () => void;
  // 発話が `minSpeechMs` (200ms) 続いたときの通知 (spec 2026-07-28 D11 と同じ層)。
  // **`onVADMisfire` とは排他** — 200ms に届かなかった音は misfire に落ちるので、
  // こちらは鳴らない。読み上げを止めるかどうかはこれで判断する。
  onSpeechRealStart?: () => void;
  // 発話終了の通知 (課題6/Approach A: 発話が終わった瞬間に UI を「確認中」へ
  // 前倒しするために使う。Scribe の確定を待たない)。認識自体の挙動は変えない。
  //
  // 引数は VAD が渡した 16 kHz mono の PCM そのもの (spec 2026-07-29 §4.1)。
  // **この関数は保存先を知らない** — 呼び出し側 (NameCapture) が WAV に包んで R2 へ送るのに
  // 使うだけで、ここに storage の知識は持ち込まない (原則 4)。引数を無視する既存の
  // コールバック (usePreflightVoice 由来) は反変性でそのまま代入できるので無変更で通る。
  onSpeechEnd?: (audio: Float32Array) => void;
  timeoutMs?: number;
  signal?: AbortSignal;
};

const DEFAULT_TIMEOUT_MS = 20000;

export async function recognizeSingleUtterance(opts: SingleUtteranceOpts): Promise<string> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  // scribe/detach を bare `let` にすると、Promise executor の中でしか代入されない
  // 変数を finally (optional chaining 経由) で読む形に対し TypeScript の
  // control-flow analysis が型を `never` に誤 narrowing する既知の癖がある
  // (tsc -b で再現確認済。ロジック上のバグではなく純粋な型解析の制約)。
  // オブジェクトのプロパティ代入にすることでこの誤 narrowing を回避する。
  const handles: { scribe: StreamingSttClient | null; detach: (() => void) | null } = {
    scribe: null,
    detach: null,
  };
  let timer: ReturnType<typeof setTimeout> | null = null;
  let onAbort: (() => void) | null = null;
  // client.connect() / mic.ensure() は非同期 (fetch + WebSocket handshake /
  // Silero モデルロード) を挟むため、その await 中に timeout/abort/onError で
  // 先に settle してしまうケースがあり得る (例: MediaStreamManager.switchDevice()
  // による abort が token fetch 中に飛んでくる)。その場合でも finally の
  // scribe?.close() は「まだ生成されていない WS」に対して呼ばれるため no-op になり、
  // 後から connect() が完了すると誰も片付けない WS 接続が残ってしまう
  // (呼び直しのたびに積み上がるリーク)。settled フラグで「既に決着した後の完了」を
  // 検知し、その場で追加の teardown を行う。
  //
  // **VAD についてはこの後始末が要らなくなった** — マイクは持ち主 (usePreflightVoice)
  // の持ち物で、この関数は借りているだけなので、決着しても destroy してはいけない。
  let settled = false;

  try {
    return await new Promise<string>((resolve, reject) => {
      const settleResolve = (text: string) => {
        if (settled) return;
        settled = true;
        resolve(text);
      };
      const settleReject = (e: unknown) => {
        if (settled) return;
        settled = true;
        reject(e);
      };

      if (opts.signal?.aborted) {
        settleReject(new AppError("stt-failed", "キャンセルされました"));
        return;
      }
      timer = setTimeout(() => {
        settleReject(
          new AppError("stt-failed", "音声を認識できませんでした。もう一度お試しください。")
        );
      }, timeoutMs);
      onAbort = () => settleReject(new AppError("stt-failed", "キャンセルされました"));
      opts.signal?.addEventListener("abort", onAbort);

      const client = makeSttClient({
        onPartial: (text) => opts.onPartial?.(text),
        onCommitted: (text) => settleResolve(text),
        onError: () =>
          settleReject(new AppError("stt-failed", "リアルタイム文字起こしに失敗しました。")),
      });
      handles.scribe = client;

      void (async () => {
        try {
          await client.connect([]);
          if (settled) {
            // connect() が解決した時点で既に timeout/abort/onError 経由で決着済み。
            // finally の scribe?.close() はまだ ws が無い状態で呼ばれて no-op だったため、
            // 今できたばかりの接続をここで確実に閉じる。
            client.close();
            return;
          }
          // **起動の失敗はここで reject に落ちる。** VadController の onError は
          // start() の catch からしか呼ばれないので、旧実装が onError で拾っていた
          // 経路と行き先は同じ (registerFailure → 3 回で retry-prompt + キーボード)。
          await opts.mic.ensure();
          if (settled) return;
          // **attach は connect と ensure の後**。先に並ぶと、WS が開く前に届いた
          // セグメントを `sendAudio` が握り潰し (readyState !== 1 で no-op)、
          // 発話が消えたまま 20 秒の timeout を待つことになる。
          handles.detach = opts.mic.attach({
            onSpeechStart: () => opts.onSpeechStart?.(),
            onSpeechRealStart: () => opts.onSpeechRealStart?.(),
            onSpeechEnd: (audio) => {
              // 発話終了を呼び出し側へ通知 (課題6: UI を「確認中」へ前倒し / 2026-07-29:
              // 氏名音声の保存)。PCM は加工せずそのまま渡す。認識挙動は変えない。
              opts.onSpeechEnd?.(audio);
              // VAD の 16 kHz Float32Array をそのまま送る (自前 downsample 不要)
              client.sendAudio(audio);
              client.commit();
            },
          });
        } catch (e) {
          settleReject(e);
        }
      })();
    });
  } finally {
    if (timer !== null) clearTimeout(timer);
    if (onAbort) opts.signal?.removeEventListener("abort", onAbort);
    // **マイクは destroy しない** — 持ち主のものを借りているだけで、壊すと
    // 1 発話ごとの作り直しに戻る (この変更が消そうとしている当のもの)。
    handles.detach?.();
    handles.scribe?.close();
  }
}
